import React, { useEffect, useState } from 'react';
import { PLAYER } from '../playerData';
import { MIN_PLAYERS, MAX_PLAYERS } from './config';
import { BOT_NAMES } from './botNames';
import { ASSETS } from '../assetManifest';

function Lobby({ gameInterface, onFinishGame }) {
  const [usersList, setUsersList] = useState([]);
  const [lobbyId, setLobbyId] = useState(gameInterface?.lobbyId);
  const [inputLobbyId, setInputLobbyId] = useState('');
  const [joinError, setJoinError] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  useEffect(() => {
    const updateUsers = (data) => {
      setUsersList(Object.values(gameInterface.users));
      setLobbyId(data.lobby_id);
    };

    const handleJoinLobbyFailed = (error) => {
      setJoinError(error);
      setIsJoining(false);
    };

    const handleConnected = () => {
      console.log('Lobby: Connected to server, attempting to join lobby');
      if (!gameInterface.lobbyId && !isJoining) {
        setIsJoining(true);
        gameInterface.joinLobby();
      }
    };

    gameInterface.on('player_list_changed', updateUsers);
    gameInterface.on('join_lobby_failed', handleJoinLobbyFailed);
    gameInterface.on('connected', handleConnected);

    if (gameInterface.isConnected) {
      handleConnected();
    }

    return () => {
      if (gameInterface.callbacks.player_list_changed === updateUsers) {
        gameInterface.callbacks.player_list_changed = null;
      }
      if (gameInterface.callbacks.join_lobby_failed === handleJoinLobbyFailed) {
        gameInterface.callbacks.join_lobby_failed = null;
      }
      if (gameInterface.callbacks.connected === handleConnected) {
        gameInterface.callbacks.connected = null;
      }
    };
  }, [gameInterface, isJoining]);

  const handleLobbyIdChange = (e) => {
    setInputLobbyId(e.target.value.toUpperCase());
  };

  const handleJoinLobby = () => {
    setJoinError('');
    gameInterface.joinLobby(inputLobbyId.trim() || null);
  };

  const handleStartGame = () => {
    if (usersList.length < MAX_PLAYERS) {
      const currentPlayers = Object.values(gameInterface.users).length;
      const botsNeeded = MAX_PLAYERS - currentPlayers;
      
      for (let i = 0; i < botsNeeded; i++) {
        const botId = `bot-${i}`;
        
        gameInterface.users[botId] = {
          sid: botId,
          name: `Bot ${i + 1}`,
          stats: { ...PLAYER.stats },
          is_bot: true,
          lobby_id: lobbyId
        };
      }
    }
    gameInterface.sendStartGame();
  };

  return (
    <div className="h-full w-full relative">
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${ASSETS.backgrounds.lobby_background.path})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          top: '-209px',
          left: '-731px',
          width: '1797px',
          height: '1198px'
        }}
      />
      
      <div className="relative z-10 flex flex-col items-center pt-[23px]">
        <div className="w-[307px] h-[87px] relative">
          <span className="flex w-[307px] h-[50px] justify-center items-center font-['Asap'] text-[24px] font-bold leading-[36.672px] text-[#1f6ad9]">
            Join or Create a Race
          </span>
          {lobbyId && (
            <div className="flex items-center justify-center">
              <span className="font-['Asap'] text-[20px] font-bold leading-[27.504px] text-[#083374] text-right">
                Your Lobby ID:{" "}
              </span>
              <span className="font-['Asap'] text-[20px] font-bold leading-[27.504px] text-[#fe48cd] ml-[5px]">
                {lobbyId}
              </span>
            </div>
          )}
        </div>

        <div 
          className="w-[374px] h-[271px] relative"
          style={{
            backgroundImage: `url(${ASSETS.backgrounds.lobby_join_section.path})`,
            backgroundSize: 'cover'
          }}
        >
          <span className="flex w-[345px] h-[55px] justify-center items-center font-['Asap'] text-[20px] font-bold leading-[22.92px] text-white mt-[79px] mx-auto text-center" style={{textShadow: '1.5px 1.5px 1.5px rgba(0,0,0,0.6)'}}>
            Enter a race id to join a friend's lobby
          </span>
          <input
            type="text"
            value={inputLobbyId}
            onChange={handleLobbyIdChange}
            className="w-[299px] h-[47px] bg-[rgba(8,84,203,0.4)] rounded-[25px] border-solid border border-white mx-auto block px-4 text-white font-['Asap']"
          />
          <button
            onClick={handleJoinLobby}
            className="w-[128px] h-[55px] mx-auto block mt-[19px] flex justify-center items-center"
            style={{
              backgroundImage: `url(${ASSETS.ui.button_background.path})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              width: '128px',
              height: '55px'
            }}
          >
            <span className="font-['Asap'] text-[16px] font-bold leading-[22.92px] text-white flex-1 text-center ml-[8px]">
              Join Lobby
            </span>
          </button>
        </div>

        <div 
          className="w-[348px] h-[292px] mt-[8px]"
          style={{
            backgroundImage: `url(${ASSETS.backgrounds.lobby_players_panel.path})`,
            backgroundSize: '100% 100%'
          }}
        >
          <span className="flex w-[233px] h-[50px] justify-center items-center font-['Asap'] text-[20px] font-bold leading-[27.504px] text-white mx-auto mt-[7px]">
            Connected Players
          </span>
          <div className="mt-2 ml-[22px]">
            {usersList.map((user) => (
              <div 
                key={user.sid}
                className="font-['Asap'] text-[20px] font-bold leading-[22.92px] text-[#f4fbff] h-[26px]"
              >
                {user.name}
                {user.is_host && ' (Host)'}
              </div>
            ))}
          </div>
        </div>

        {gameInterface.userId === gameInterface.hostId && (
          <button
            onClick={handleStartGame}
            disabled={usersList.length < MIN_PLAYERS}
            className="w-[128px] h-[55px] mt-[20px] flex justify-center items-center"
            style={{
              backgroundImage: `url(${ASSETS.ui.button_background.path})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              width: '128px',
              height: '55px',
              opacity: usersList.length < MIN_PLAYERS ? 0.5 : 1
            }}
          >
            <span className="font-['Asap'] text-[16px] font-bold leading-[22.92px] text-white flex-1 text-center">
              Start
            </span>
          </button>
        )}

        <span className="flex w-[233px] h-[50px] justify-center items-center font-['Asap'] text-[24px] font-bold leading-[27.504px] text-white mt-[8px]">
          Players : {usersList.length}/{MAX_PLAYERS}
        </span>

        <button
          onClick={onFinishGame}
          className="absolute bottom-4 left-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-['Asap']"
        >
          Leave
        </button>
      </div>
    </div>
  );
}

export default Lobby;
